import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import {
  Copy,
  Download,
  ExternalLink,
  RefreshCw,
  Share2,
} from "lucide-react";

const WEBMENU_BASE_URL = "https://webmenu.megriva.com";

export default function QRCodePage() {
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadBusiness = async () => {
    try {
      setLoading(true);

      const rawUser = localStorage.getItem("user");

      if (!rawUser) {
        toast.error("Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.");
        setBusiness(null);
        return;
      }

      let user;

      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error("user parse hatası:", e);
        toast.error("Kullanıcı bilgisi okunamadı.");
        setBusiness(null);
        return;
      }

      const businessId =
        user.businessId ?? user.business_id ?? user.BusinessId;

      if (!businessId) {
        toast.error("İşletme bilgisi bulunamadı.");
        setBusiness(null);
        return;
      }

      const res = await api.getBusinessById(businessId);
      const businessData = res?.data ?? null;

      setBusiness(businessData);
    } catch (error) {
      console.error("İşletme bilgisi alınırken hata:", error);
      toast.error("İşletme bilgileri alınırken bir hata oluştu.");
      setBusiness(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const businessSlug = useMemo(() => {
    return (
      business?.subDomain ||
      business?.SubDomain ||
      business?.businessSubDomain ||
      business?.business_sub_domain ||
      business?.slug ||
      ""
    )
      .toString()
      .trim();
  }, [business]);

  const menuUrl = useMemo(() => {
    if (!businessSlug) return "";
    return `${WEBMENU_BASE_URL}/${encodeURIComponent(businessSlug)}`;
  }, [businessSlug]);

  const downloadCanvas = (canvasId) => {
    if (!menuUrl) {
      toast.error("QR linki oluşturulamadı.");
      return;
    }

    const canvas = document.getElementById(canvasId);

    if (!canvas) {
      toast.error("QR kod hazırlanamadı.");
      return;
    }

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `webmenu-qr-${
      businessSlug || business?.id || "business"
    }.png`;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleDownloadQR = () => {
    downloadCanvas("business-qr");
  };

  const handleDownloadMobileQR = () => {
    downloadCanvas("business-qr-mobile");
  };

  const handleOpenMenu = () => {
    if (!menuUrl) {
      toast.error("Web Menü bağlantısı oluşturulamadı.");
      return;
    }

    window.open(menuUrl, "_blank", "noopener,noreferrer");
  };

  const copyMenuUrl = async () => {
    if (!menuUrl) {
      toast.error("Web Menü bağlantısı oluşturulamadı.");
      return false;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(menuUrl);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = menuUrl;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";

        document.body.appendChild(textarea);
        textarea.select();

        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);

        if (!copied) {
          throw new Error("Kopyalama desteklenmiyor.");
        }
      }

      toast.success("Web Menü bağlantısı kopyalandı.");
      return true;
    } catch (error) {
      console.error("MENU URL COPY ERROR:", error);
      toast.error("Web Menü bağlantısı kopyalanamadı.");
      return false;
    }
  };

  const handleShareMenu = async () => {
    if (!menuUrl) {
      toast.error("Web Menü bağlantısı oluşturulamadı.");
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${business?.name || "İşletme"} Web Menü`,
          text: `${business?.name || "İşletme"} dijital vitrini`,
          url: menuUrl,
        });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("MENU SHARE ERROR:", error);
      }
    }

    await copyMenuUrl();
  };

  const handleShareQRImage = async () => {
    if (!menuUrl) {
      toast.error("QR linki oluşturulamadı.");
      return;
    }

    const canvas = document.getElementById("business-qr-mobile");

    if (!canvas) {
      toast.error("QR kod hazırlanamadı.");
      return;
    }

    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) {
            resolve(result);
            return;
          }

          reject(new Error("QR görseli oluşturulamadı."));
        }, "image/png");
      });

      const file = new File(
        [blob],
        `webmenu-qr-${businessSlug || business?.id || "business"}.png`,
        { type: "image/png" }
      );

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          title: `${business?.name || "İşletme"} Web Menü`,
          text: `${business?.name || "İşletme"} dijital vitrini`,
          files: [file],
        });
        return;
      }

      handleDownloadMobileQR();
      toast.success("QR kod indirildi.");
    } catch (error) {
      if (error?.name === "AbortError") return;

      console.error("QR IMAGE SHARE ERROR:", error);
      handleDownloadMobileQR();
    }
  };

  return (
    <AdminLayout>
      {/* =========================================================
          MOBILE WEB
          Browser-native actions + app-consistent information hierarchy.
         ========================================================= */}
      <div className="min-h-full bg-[#F8F8F8] px-5 pb-10 pt-4 md:hidden">
        {loading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E4E4E7] border-t-[#EA580C]" />
            <p className="mt-3 text-sm text-[#71717A]">QR kod hazırlanıyor...</p>
          </div>
        ) : !business ? (
          <div className="rounded-[20px] border border-red-200 bg-red-50 p-5">
            <p className="text-sm font-bold text-red-800">
              İşletme bilgileri alınamadı.
            </p>

            <button
              type="button"
              onClick={loadBusiness}
              className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-red-200 bg-white px-4 text-sm font-semibold text-red-700 active:bg-red-50"
            >
              <RefreshCw className="h-4 w-4" />
              Tekrar Dene
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-3 px-0.5">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-extrabold tracking-[0.08em] text-[#EA580C]">
                  QR MENÜ
                </p>

                <h1 className="mt-0.5 truncate text-[22px] font-extrabold leading-7 text-[#18181B]">
                  {business.name || "İşletme"}
                </h1>

                {business.address ? (
                  <p className="mt-0.5 truncate text-xs leading-[17px] text-[#71717A]">
                    {business.address}
                  </p>
                ) : null}
              </div>

              <div className="flex min-h-8 shrink-0 items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-2.5">
                <span className="h-[7px] w-[7px] rounded-full bg-green-500" />
                <span className="text-[11px] font-bold text-[#52525B]">
                  Hazır
                </span>
              </div>
            </div>

            {menuUrl ? (
              <div className="rounded-[22px] border border-[#E4E4E7] bg-white p-4">
                <div className="mb-3.5">
                  <h2 className="text-lg font-extrabold text-[#18181B]">
                    Web Menü QR kodu
                  </h2>
                  <p className="mt-1 max-w-[310px] text-xs leading-[17px] text-[#71717A]">
                    Masada, tezgahta veya sosyal medyada kullanabilirsiniz.
                  </p>
                </div>

                <div className="flex justify-center">
                  <div className="rounded-[18px] border border-[#E4E4E7] bg-white p-3">
                    <QRCodeCanvas
                      id="business-qr-mobile"
                      value={menuUrl}
                      size={224}
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-center gap-2 px-2">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#EA580C]" />
                  <p className="text-center text-xs leading-[17px] text-[#71717A]">
                    Telefon kamerasıyla okutulduğunda dijital vitrininiz açılır
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleOpenMenu}
                  className="mt-4 flex min-h-[58px] w-full items-center gap-3 rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 text-left active:bg-[#F4F4F5]"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.04em] text-[#A1A1AA]">
                      Web Menü adresi
                    </p>
                    <p className="mt-0.5 truncate text-[13px] font-semibold leading-[18px] text-[#52525B]">
                      {menuUrl}
                    </p>
                  </div>

                  <ExternalLink className="h-4 w-4 shrink-0 text-[#A1A1AA]" />
                </button>

                <button
                  type="button"
                  onClick={handleOpenMenu}
                  className="mt-3.5 flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[15px] bg-[#EA580C] px-4 text-[15px] font-bold text-white active:opacity-70"
                >
                  <ExternalLink className="h-4 w-4" />
                  Vitrini Aç
                </button>

                <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={handleShareQRImage}
                    className="flex min-h-12 items-center justify-center gap-2 rounded-[15px] border border-[#E4E4E7] bg-white px-2 text-sm font-bold text-[#3F3F46] active:bg-[#F4F4F5]"
                  >
                    <Share2 className="h-4 w-4" />
                    QR'ı Paylaş
                  </button>

                  <button
                    type="button"
                    onClick={handleShareMenu}
                    className="flex min-h-12 items-center justify-center gap-2 rounded-[15px] border border-[#E4E4E7] bg-white px-2 text-sm font-bold text-[#3F3F46] active:bg-[#F4F4F5]"
                  >
                    <Share2 className="h-4 w-4" />
                    Linki Paylaş
                  </button>
                </div>

                <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={handleDownloadMobileQR}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-[14px] px-2 text-xs font-semibold text-[#71717A] active:bg-[#F4F4F5]"
                  >
                    <Download className="h-4 w-4" />
                    QR'ı İndir
                  </button>

                  <button
                    type="button"
                    onClick={copyMenuUrl}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-[14px] px-2 text-xs font-semibold text-[#71717A] active:bg-[#F4F4F5]"
                  >
                    <Copy className="h-4 w-4" />
                    Linki Kopyala
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-[22px] border border-[#E4E4E7] bg-white px-6 py-12 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#FFF7ED] text-xl font-extrabold text-[#EA580C]">
                  !
                </div>
                <h2 className="mt-3.5 text-[17px] font-bold text-[#18181B]">
                  QR kod oluşturulamıyor
                </h2>
                <p className="mt-1.5 text-[13px] text-[#71717A]">
                  İşletme menü bağlantısı bulunamadı.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* =========================================================
          DESKTOP
          Premium Web Menü QR & bağlantı görünümü
         ========================================================= */}
      <div className="mx-auto hidden w-full max-w-[1180px] space-y-6 md:block">
        <section className="relative overflow-hidden rounded-[30px] border border-zinc-200/80 bg-white p-7 shadow-[0_22px_65px_rgba(24,24,27,0.05)]">
          <div
            aria-hidden="true"
            className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-orange-100/80 blur-3xl"
          />

          <div className="relative flex items-start justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.13em] text-orange-700">
                <span className="h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
                QR Kod & Bağlantı
              </div>

              <h1 className="mt-5 text-[34px] font-extrabold tracking-[-0.04em] text-zinc-950 lg:text-[40px]">
                Dijital vitrininizi tek QR kodla paylaşın.
              </h1>

              <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-zinc-500">
                Web Menü bağlantınızı QR kod, doğrudan link veya paylaşım
                seçenekleriyle müşterilerinize ulaştırın.
              </p>
            </div>

            {!loading && business ? (
              <div className="min-w-[250px] rounded-[22px] border border-zinc-200 bg-[#FAFAF9] p-4">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-zinc-400">
                  İşletme
                </p>
                <p className="mt-1.5 truncate text-sm font-extrabold text-zinc-900">
                  {business?.name || "İşletmeniz"}
                </p>
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.11em] text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {menuUrl ? "Paylaşıma hazır" : "Bağlantı bekleniyor"}
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {loading ? (
          <section className="flex min-h-[360px] items-center justify-center rounded-[28px] border border-zinc-200/80 bg-white">
            <div className="text-center">
              <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-zinc-200 border-t-[#EA580C]" />
              <p className="mt-3 text-sm font-medium text-zinc-500">
                QR kod hazırlanıyor...
              </p>
            </div>
          </section>
        ) : !business ? (
          <section className="flex min-h-[320px] flex-col items-center justify-center rounded-[28px] border border-red-200 bg-red-50/60 p-8 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-[18px] bg-white text-red-600 shadow-sm">
              <RefreshCw className="h-6 w-6" />
            </div>

            <h2 className="mt-5 text-lg font-extrabold text-zinc-950">
              İşletme bilgileri alınamadı
            </h2>

            <p className="mt-2 max-w-md text-sm font-medium leading-6 text-zinc-500">
              QR kod oluşturabilmek için işletme bilgilerinin yüklenmesi gerekiyor.
            </p>

            <Button
              type="button"
              onClick={loadBusiness}
              className="mt-5 rounded-full bg-zinc-950 px-5 font-bold text-white hover:bg-zinc-800"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Tekrar Dene
            </Button>
          </section>
        ) : menuUrl ? (
          <section className="grid gap-6 xl:grid-cols-[0.88fr_1.12fr]">
            <div className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                  QR Kod
                </p>
                <h2 className="mt-2 text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                  Tarat ve vitrini aç
                </h2>
                <p className="mt-1 text-sm font-medium leading-6 text-zinc-500">
                  Masa, tezgah, katalog, kartvizit veya sosyal medya görsellerinizde
                  kullanabilirsiniz.
                </p>
              </div>

              <div className="mt-6 flex justify-center">
                <div className="rounded-[28px] border border-zinc-200 bg-white p-5 shadow-[0_16px_40px_rgba(24,24,27,0.06)]">
                  <QRCodeCanvas
                    id="business-qr"
                    value={menuUrl}
                    size={280}
                    includeMargin
                  />
                </div>
              </div>

              <div className="mt-6 rounded-[18px] border border-orange-100 bg-orange-50/60 p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#EA580C]" />
                  <div>
                    <p className="text-sm font-extrabold text-zinc-900">
                      Tek QR, sürekli güncel içerik
                    </p>
                    <p className="mt-1 text-xs font-medium leading-5 text-zinc-500">
                      Web Menü içeriğinizi güncellediğinizde QR kodu yeniden
                      oluşturmanız gerekmez.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-[28px] border border-zinc-200/80 bg-white p-6 shadow-[0_18px_55px_rgba(24,24,27,0.04)]">
                <div className="flex items-start justify-between gap-5">
                  <div>
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#EA580C]">
                      Web Menü Bağlantısı
                    </p>
                    <h2 className="mt-2 text-xl font-extrabold tracking-[-0.025em] text-zinc-950">
                      Paylaşmaya hazır bağlantınız
                    </h2>
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.11em] text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Canlı
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleOpenMenu}
                  className="mt-5 flex w-full items-center gap-4 rounded-[20px] border border-zinc-200 bg-[#FAFAF9] p-4 text-left transition hover:border-orange-200 hover:bg-white"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-zinc-400">
                      Adres
                    </p>
                    <p className="mt-1.5 truncate text-sm font-bold text-zinc-800">
                      {menuUrl}
                    </p>
                  </div>

                  <ExternalLink className="h-5 w-5 shrink-0 text-zinc-400" />
                </button>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Button
                    type="button"
                    onClick={handleOpenMenu}
                    className="h-11 rounded-full bg-[#EA580C] font-bold text-white hover:bg-[#C2410C]"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Vitrini Aç
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={copyMenuUrl}
                    className="h-11 rounded-full border-zinc-200 bg-white font-bold text-zinc-800 hover:bg-zinc-50"
                  >
                    <Copy className="mr-2 h-4 w-4" />
                    Linki Kopyala
                  </Button>
                </div>
              </div>

              <div className="rounded-[28px] border border-zinc-200/80 bg-white p-6">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-zinc-400">
                  Paylaşım Araçları
                </p>

                <div className="mt-4 grid gap-3">
                  <button
                    type="button"
                    onClick={handleDownloadQR}
                    className="group flex min-h-[72px] items-center gap-4 rounded-[18px] border border-zinc-200 bg-[#FAFAF9] p-4 text-left transition hover:border-orange-200 hover:bg-white"
                  >
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-white text-zinc-600 shadow-sm ring-1 ring-zinc-100 transition group-hover:bg-[#EA580C] group-hover:text-white">
                      <Download className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-extrabold text-zinc-900">
                        QR Kodunu İndir
                      </p>
                      <p className="mt-1 text-xs font-medium text-zinc-500">
                        PNG olarak kaydedip baskı veya dijital kullanımda değerlendirin.
                      </p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={handleShareMenu}
                    className="group flex min-h-[72px] items-center gap-4 rounded-[18px] border border-zinc-200 bg-[#FAFAF9] p-4 text-left transition hover:border-orange-200 hover:bg-white"
                  >
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-white text-zinc-600 shadow-sm ring-1 ring-zinc-100 transition group-hover:bg-zinc-950 group-hover:text-white">
                      <Share2 className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-extrabold text-zinc-900">
                        Bağlantıyı Paylaş
                      </p>
                      <p className="mt-1 text-xs font-medium text-zinc-500">
                        Tarayıcınız destekliyorsa paylaşım menüsünü doğrudan açın.
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="rounded-[28px] bg-zinc-950 p-6 text-white shadow-[0_22px_60px_rgba(24,24,27,0.14)]">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-orange-300">
                  İşletme Bilgisi
                </p>
                <p className="mt-3 text-lg font-extrabold">
                  {business?.name || "İşletmeniz"}
                </p>
                <p className="mt-1 text-sm font-medium leading-6 text-zinc-400">
                  {business?.address || "Adres bilgisi eklenmemiş"}
                </p>
              </div>
            </div>
          </section>
        ) : (
          <section className="flex min-h-[320px] flex-col items-center justify-center rounded-[28px] border border-zinc-200/80 bg-white p-8 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-[18px] bg-orange-50 text-[#EA580C]">
              <ExternalLink className="h-6 w-6" />
            </div>

            <h2 className="mt-5 text-lg font-extrabold text-zinc-950">
              Web Menü bağlantısı bulunamadı
            </h2>

            <p className="mt-2 max-w-md text-sm font-medium leading-6 text-zinc-500">
              İşletme subdomain bilgisi oluştuğunda QR kodunuz otomatik olarak
              kullanılabilir hale gelecektir.
            </p>
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
